
import { Button, Grid, Typography } from "@material-ui/core";
import { CancelOutlined, CheckCircleOutline } from "@material-ui/icons";
import Authorize from "components/Authorize";
import { permissionProfiles } from "components/Authorize/permissionProfiles";
import { activityStatuses } from "features/activity/activityConsts";
import { useMoveToStatusMutation } from "features/activity/activitySlice";
import moment from "moment";
import { IconEdit } from "icons";
import { useState } from "react";
import { useSelector } from "react-redux";
import FormatService from "services/formatService";
import ActionPopup from './ActionPopup';
import { WorkOrderCardStyle } from "./WorkOrderCardStyle";
import { useApproveLoadPriceMutation } from "features/loads/loadsSlice";
import { LoadPriceStatusEnum } from "features/clients/clientConfigs/transports/PricingEnums";
import VendorDetachedEmailDialog from "features/vms/vendorDirectory/vendorDetachedTemplate/VendorDetachedEmailDialog";
import NotificationProvider from "components/NotificationProvider";
import ApproverNameDialog from "features/activity/components/ApproverNameDialog/ApproverNameDialog";
import { selectIsClient } from "features/user/userSlice";

const WorkOrderActions = ({ entity, handleDone, stillPending = true, fill = true, loadMode, wrapperStyles, buttonStyles }) => {
    const classes = WorkOrderCardStyle();
    const [updateStatus, { isLoadingUpdate }] = useMoveToStatusMutation();
    const [approvePrice, { isLoading: isApproving }] = useApproveLoadPriceMutation();

    const isClient = useSelector(selectIsClient);

    const [openApproveAction, setOpenApproveAction] = useState(false);
    const [openRejectAction, setOpenRejectAction] = useState(false);
    const [error, setError] = useState(null);
    const [isLoadingAction, setIsLoadingAction] = useState(false);
    const [emailDraft, setEmailDraft] = useState(null);
    const [approverNameDialogOpen, setApproverNameDialogOpen] = useState(false);
    const [approverName, setApproverName] = useState('');

    const moveToStatus = async (newStatus, approverNameOverride) => {
        setIsLoadingAction(true);
        const res = await updateStatus({
            activityLogIds: [entity?.id],
            moveToStatus: newStatus,
            approverName: approverNameOverride ?? undefined,
            activityDate: new Date(moment().format("MMM DD, yyyy, hh:mm a") + " UTC").toISOString()
        });
        if (!res || res?.error) {
            setError(`Failed to update activity.`);
            NotificationProvider.error('Failed to approve activity');
        } else {
            setError(null);
            if (res?.data?.emailDraft) {
                setEmailDraft(res.data.emailDraft);
            }
            NotificationProvider.success('Activity approved successfully');
        }
        setIsLoadingAction(false);
    }

    const acceptStatus = entity?.activityStatus == activityStatuses.PendingClientDecision
        ? activityStatuses.PendingApproval
        : activityStatuses.PricingApprovedByClient;

    const submitAccept = async (approverNameOverride) => {
        setOpenApproveAction(true);
        loadMode ? await approvePrice({ id: entity?.id, loadGroupId: entity?.groupId, status: LoadPriceStatusEnum.APPROVED, loadId: entity?.loadId })
            : await moveToStatus(acceptStatus, approverNameOverride);
    }

    const handleAccept = async () => {
        const isL1 = entity?.activityStatus == activityStatuses.PendingClientDecision && acceptStatus == activityStatuses.PendingApproval;
        const isL2 = entity?.activityStatus == activityStatuses.PendingClientApproval && acceptStatus == activityStatuses.PricingApprovedByClient;

        if (!loadMode && (isL1 || isL2) && !isClient) {
            setApproverName('');
            setApproverNameDialogOpen(true);
            return;
        }
        await submitAccept();
    }

    const onConfirmApproverName = async () => {
        setApproverNameDialogOpen(false);
        await submitAccept(approverName);
        setApproverName('');
    }

    const onCancelApproverName = () => {
        setApproverNameDialogOpen(false);
        setApproverName('');
    }

    const handleReject = async () => {
        setOpenRejectAction(true);
        loadMode ? await approvePrice({ id: entity?.id, loadGroupId: entity?.groupId, status: LoadPriceStatusEnum.REJECTED_BY_CLIENT, loadId: entity?.loadId })
            : await moveToStatus(activityStatuses.Cancelled);
    }

    const handleEdit = async () => {
        await moveToStatus(activityStatuses.PendingClientApproval);
    }

    const handleCloseActionsLoading = () => {
        setOpenApproveAction(false);
        setOpenRejectAction(false);
        setError(null);
        handleDone();
    }

    return (
        <>
            {stillPending ? <Grid item xs={4} direction="row" className={wrapperStyles ?? classes.actionWrapper}>
                <Authorize profile={loadMode ? permissionProfiles.LOAD.LOAD_APPROVE_PRICE : permissionProfiles.ACTIVITY.APPROVE_PENDING_CLIENT}>
                    <div
                        onClick={handleAccept}
                        className={buttonStyles ?? classes.actionButton}
                        style={{ backgroundColor: `${fill ? "#D4EDDA" : 'white'}` }}>
                        <div>
                            <CheckCircleOutline style={{ fill: '#82cc80' }} />
                            Accept
                        </div>
                    </div>
                    <div
                        onClick={handleReject}
                        className={buttonStyles ?? `${classes.actionButton} ${classes.rejectButton}`}
                        style={{ backgroundColor: `${fill ? "#F8D7DA" : 'white'}` }}>
                        <div>
                            <CancelOutlined style={{ fill: '#e15b56' }} />
                            Reject
                        </div>
                    </div>
                </Authorize>
            </Grid> :
                <Grid item xs={4} className={classes.editButtonWrapper}>
                    <Typography className={classes.lightText} style={{ padding: '0.5rem' }}>
                        {FormatService.formatDateNoConversion(entity?.activityDate)}
                    </Typography>
                    <Button
                        className={classes.editButton}
                        variant="fill"
                        startIcon={<IconEdit />}
                        onClick={handleEdit}>
                        Edit
                    </Button>
                </Grid>}
            <ActionPopup open={openApproveAction} handleClose={handleCloseActionsLoading} loading={isLoadingAction} error={error} loadingText="Approving..." confirmationText={loadMode ? 'Load price approved' : "Work Order Approved"} />
            <ActionPopup open={openRejectAction} handleClose={handleCloseActionsLoading} loading={isLoadingAction} error={error} loadingText="Rejecting..." confirmationText={loadMode ? 'Load price rejected' : "Work Order Rejected"} confirmationIcon={<CancelOutlined style={{ fill: '#e15b56' }} />} />
            <VendorDetachedEmailDialog
                open={!!emailDraft}
                onClose={() => setEmailDraft(null)}
                emailDraft={emailDraft}
                activityLogId={entity?.id}
            />
            <ApproverNameDialog
                open={approverNameDialogOpen}
                onConfirm={onConfirmApproverName}
                onCancel={onCancelApproverName}
                value={approverName}
                onChange={setApproverName}
                clientAccountId={entity?.clientAccountId}
            />
        </>
    )
}

export default WorkOrderActions;